// assets/infinite-scroll.js

class CollectionInfiniteScroll {
  constructor(container) {
    this.container = container;
    this.productGrid = document.getElementById('product-grid');
    this.spinner = container.querySelector('.infinite-scroll__spinner');
    this.observer = null;
    this.nextUrl = container.dataset.nextUrl;
    this.sectionId = container.dataset.sectionId;
    this.isLoading = false;
    this.abortController = null;
    this.placeholderNodes = [];

    if (!this.productGrid || !this.nextUrl) return;

    this.initObserver();
  }

  getPrefetchMargin() {
    return window.matchMedia('(max-width: 749px)').matches
      ? '0px 0px 800px 0px'
      : '0px 0px 1200px 0px';
  }

  getPlaceholderCount() {
    const desktopCols = this.getDesktopColumnCount();
    return window.matchMedia('(max-width: 749px)').matches ? 4 : desktopCols * 2;
  }

  getDesktopColumnCount() {
    const classes = Array.from(this.productGrid.classList);
    const match = classes
      .map((cls) => cls.match(/^grid--(\d+)-col-desktop$/))
      .find(Boolean);

    return match ? parseInt(match[1], 10) : 4;
  }

  initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          this.loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: this.getPrefetchMargin(),
        threshold: 0
      }
    );

    this.observer.observe(this.container);
  }

  resetObserver() {
    if (!this.observer || !this.container || !this.container.isConnected) return;
    this.observer.unobserve(this.container);
    this.observer.observe(this.container);
  }

  shouldLoadAgain() {
    if (!this.container || !this.nextUrl || this.isLoading) return false;

    const rect = this.container.getBoundingClientRect();
    const preloadDistance = window.matchMedia('(max-width: 749px)').matches ? 800 : 1200;

    return rect.top <= window.innerHeight + preloadDistance;
  }

  createPlaceholders(count = this.getPlaceholderCount()) {
    if (!this.productGrid || this.placeholderNodes.length) return;

    const fragment = document.createDocumentFragment();
    const nodes = [];

    for (let i = 0; i < count; i += 1) {
      const li = document.createElement('li');
      li.className = 'grid__item infinite-scroll-placeholder';
      li.setAttribute('aria-hidden', 'true');

      li.innerHTML = `
        <div class="card-wrapper product-card-wrapper">
          <div class="card card--standard card--media">
            <div class="card__inner">
              <div class="media infinite-scroll-placeholder__media"></div>
            </div>
            <div class="card__content">
              <div class="infinite-scroll-placeholder__line infinite-scroll-placeholder__line--title"></div>
              <div class="infinite-scroll-placeholder__line infinite-scroll-placeholder__line--price"></div>
              <div class="infinite-scroll-placeholder__button"></div>
            </div>
          </div>
        </div>
      `;

      fragment.appendChild(li);
      nodes.push(li);
    }

    this.productGrid.appendChild(fragment);
    this.placeholderNodes = nodes;
  }

  removePlaceholders() {
    if (!this.placeholderNodes.length) return;
    this.placeholderNodes.forEach((node) => node.remove());
    this.placeholderNodes = [];
  }

  buildRequestUrl() {
    const url = new URL(this.nextUrl, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    url.searchParams.set('section_id', this.sectionId);

    return url.toString();
  }

  loadNextPage() {
    if (!this.nextUrl || this.isLoading) return;

    this.isLoading = true;

    if (this.spinner) this.spinner.hidden = false;

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.createPlaceholders();

    fetch(this.buildRequestUrl(), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network error while loading more products');
        }
        return response.text();
      })
      .then((htmlText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        const newGrid = doc.querySelector('#product-grid');
        if (!newGrid) {
          this.nextUrl = null;
          this.removePlaceholders();
          this.cleanup();
          return;
        }

        const newItems = newGrid.querySelectorAll('.grid__item');
        const fragment = document.createDocumentFragment();

        newItems.forEach((item) => {
          fragment.appendChild(item);
        });

        this.removePlaceholders();
        this.productGrid.appendChild(fragment);

        const newInfinite = doc.querySelector('#InfiniteScroll');
        if (newInfinite && newInfinite.dataset.nextUrl) {
          this.nextUrl = newInfinite.dataset.nextUrl;
          this.resetObserver();

          if (this.shouldLoadAgain()) {
            requestAnimationFrame(() => {
              this.loadNextPage();
            });
          }
        } else {
          this.nextUrl = null;
          this.cleanup();
        }

        try {
          if (
            window.Shopify &&
            Shopify.theme &&
            typeof Shopify.theme.initScrollAnimation === 'function'
          ) {
            Shopify.theme.initScrollAnimation();
          }
        } catch (e) {
          console.warn('Scroll animation re-init failed', e);
        }
      })
      .catch((error) => {
        this.removePlaceholders();

        if (error.name === 'AbortError') return;

        console.error('[InfiniteScroll] Error loading next page', error);
        this.cleanup();
      })
      .finally(() => {
        this.isLoading = false;
        if (this.spinner) this.spinner.hidden = true;
      });
  }

  cleanup() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.removePlaceholders();

    if (this.container && this.container.isConnected) {
      this.container.remove();
    }
  }
}

/**
 * Global initializer used:
 * - on first page load
 * - after filters/sorting re-render the ProductGridContainer via facets.js
 */
window.initCollectionInfiniteScroll = function () {
  const container = document.getElementById('InfiniteScroll');
  if (!container) return;

  if (
    container._infiniteScrollInstance &&
    typeof container._infiniteScrollInstance.cleanup === 'function'
  ) {
    container._infiniteScrollInstance.cleanup();
  }

  container._infiniteScrollInstance = new CollectionInfiniteScroll(container);
};

document.addEventListener('DOMContentLoaded', () => {
  window.initCollectionInfiniteScroll();
});